"use client";

import { useMemo } from "react";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export type ModalVariant = "dialog" | "sheet" | "auto";
export type ModalSheetSide = "left" | "right" | "top" | "bottom";

const DEFAULT_MOBILE_BREAKPOINT_PX = 640;
const DEFAULT_SHEET_SIDE: ModalSheetSide = "right";
export const DEFAULT_SHEET_OVERLAY_OPACITY = 50;
export const DEFAULT_SHEET_BACKDROP_BLUR_PX = 4;

export interface ResponsiveModalVariant {
  /** Variante resuelta considerando la preferencia del usuario. */
  variant: ModalVariant;
  /** Lado del sheet cuando la variante resuelve a sheet. */
  sheetSide: ModalSheetSide;
  /** Breakpoint usado para decidir mobile vs desktop en variant="auto". */
  mobileBreakpointPx: number;
  /** true cuando el viewport actual está por debajo del breakpoint mobile. */
  isMobileViewport: boolean;
  /** true cuando, dada la preferencia y el viewport, hay que renderizar Sheet. */
  shouldRenderSheet: boolean;
  /** true cuando, dada la preferencia y el viewport, hay que renderizar Drawer. */
  shouldRenderDrawer: boolean;
  /** Opacidad del overlay del sheet en porcentaje (0-100). */
  sheetOverlayOpacity: number;
  /** Intensidad del backdrop-blur del sheet en px (0-20). */
  sheetBackdropBlurPx: number;
  /** Ancho elegido arrastrando el borde del sheet, por clave de formulario. */
  sheetWidths: Record<string, number>;
}

/**
 * Resuelve la variante de modal para la UI web según la preferencia del usuario
 * (`users.uiModalPreferences`) y el viewport actual.
 *
 * Reglas:
 * - variant="auto"  -> mismo comportamiento que antes: < breakpoint => Drawer,
 *                      >= breakpoint => Dialog. La configuración del sheet
 *                      lateral NO se aplica en auto.
 * - variant="dialog" -> siempre Dialog (mobile y desktop).
 * - variant="sheet"  -> mobile sigue Drawer (UX móvil intacta), desktop usa
 *                      Sheet con el `sheetSide` configurado (default "right").
 *
 * Si el usuario aún no cargó o no tiene preferencias guardadas, se comporta
 * exactamente como la rama "auto".
 */
export function useResponsiveModalVariant(): ResponsiveModalVariant {
  const user = useCurrentUser();
  const pref = user?.uiModalPreferences;

  const breakpoint =
    pref?.mobileBreakpointPx ?? DEFAULT_MOBILE_BREAKPOINT_PX;
  const isMobileViewport = useMediaQuery(`(max-width: ${breakpoint}px)`);

  return useMemo<ResponsiveModalVariant>(() => {
    const variant: ModalVariant = pref?.variant ?? "auto";
    const sheetSide: ModalSheetSide = pref?.sheetSide ?? DEFAULT_SHEET_SIDE;
    const sheetOverlayOpacity = clamp(
      pref?.sheetOverlayOpacity,
      0,
      100,
      DEFAULT_SHEET_OVERLAY_OPACITY,
    );
    const sheetBackdropBlurPx = clamp(
      pref?.sheetBackdropBlurPx,
      0,
      20,
      DEFAULT_SHEET_BACKDROP_BLUR_PX,
    );

    const shouldRenderDrawer = isMobileViewport;
    const shouldRenderSheet = variant === "sheet" && !isMobileViewport;

    return {
      variant,
      sheetSide,
      mobileBreakpointPx: breakpoint,
      isMobileViewport,
      shouldRenderSheet,
      shouldRenderDrawer,
      sheetOverlayOpacity,
      sheetBackdropBlurPx,
      sheetWidths: pref?.sheetWidths ?? {},
    };
  }, [
    pref?.sheetWidths,
    pref?.variant,
    pref?.sheetSide,
    pref?.mobileBreakpointPx,
    pref?.sheetOverlayOpacity,
    pref?.sheetBackdropBlurPx,
    isMobileViewport,
    breakpoint,
  ]);
}

function clamp(
  value: number | undefined,
  min: number,
  max: number,
  fallback: number,
): number {
  if (value === undefined || Number.isNaN(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}